import request from "./request";

// —— 通用类型 ——

/** 统一接口响应格式，status === "0000" 表示成功 */
export interface ApiResponse<T = unknown> {
  status: string;
  message?: string;
  data?: T;
}

/** 分页请求通用入参 */
export interface PageParams {
  page?: number;
  pageSize?: number;
}

/** 分页响应通用结构 */
export interface PageResult<T> {
  list: T[];
  total: number;
  page?: number;
  pageSize?: number;
}

// —— Auth ——

export interface CaptchaGenResponse {
  status?: string;
  id: string;
  data: string;
}

export interface CaptchaVerifyResponse {
  status?: string;
  err: number;
}

export interface LoginResponse {
  status?: string;
  token?: string;
  user?: unknown;
}

export interface UserInfoResponse {
  status?: string;
  user?: unknown;
  data?: unknown;
}

export const loginByPwd = (username: string, password: string): Promise<LoginResponse> => {
  return request.post("/auth/login", { username, password }) as Promise<LoginResponse>;
};

export const loginByCode = (phone: string, code: string, inviteCode?: string): Promise<LoginResponse> => {
  return request.post("/auth/loginByCode", { phone, code, inviteCode }) as Promise<LoginResponse>;
};

export const getCaptcha = (): Promise<CaptchaGenResponse> => {
  return request.get("/captcha/gen") as Promise<CaptchaGenResponse>;
};

export const verifyCaptcha = (
  captchaId: string,
  captchaValue: string,
): Promise<CaptchaVerifyResponse> => {
  return request.post("/captcha/verify", { id: captchaId, value: captchaValue }) as Promise<CaptchaVerifyResponse>;
};

export const getVerifyCode = (
  phone: string,
  usage: string,
  captchaId: string,
  captchaValue: string,
): Promise<unknown> => {
  return request.get("/auth/code", {
    params: { phone, usage, captchaId, captchaValue },
  });
};

export const getUserInfo = (): Promise<UserInfoResponse> => {
  return request.get("/users/user/info") as Promise<UserInfoResponse>;
};

export const logout = (): Promise<unknown> => {
  return request.get("/auth/logout");
};

export interface WxghQrPayload {
  sceneCode?: string;
  scene?: string;
  qrCodeUrl?: string;
  qrUrl?: string;
  url?: string;
  qr_code?: string;
}

export const getWxghQrCode = (): Promise<{ data?: WxghQrPayload } & WxghQrPayload> => {
  return request.get("/auth/wxghQrCode") as Promise<{ data?: WxghQrPayload } & WxghQrPayload>;
};

export interface WxghLoginCheckPayload {
  token?: string;
  user?: unknown;
}

export const checkWxghLogin = (sceneCode: string): Promise<{ data?: WxghLoginCheckPayload } & WxghLoginCheckPayload> => {
  return request.get("/auth/checkWxghLogin", { params: { sceneCode } }) as Promise<
    { data?: WxghLoginCheckPayload } & WxghLoginCheckPayload
  >;
};

// —— Home / Skill ——

export interface SkillItemDto {
  id: number;
  name: string;
  version: string;
  categoryId: number;
  tagId: number;
  desp: string;
  author: string;
  origin: string;
  originDesp: string;
  downloadNum: number;
  likeNum: number;
  installNum: number;
  url: string;
  path: string;
  agentInstallDesp: string;
  humanInstallDesp: string;
  createTime: string;
  updateTime: string;
  deleted: boolean;
  deleteTime: string | null;
  userLiked: boolean;
  isRecommended?: boolean;
}

export interface PageInfoDto {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
}

export interface SkillTop50Dto {
  pageInfo: PageInfoDto;
  list: SkillItemDto[];
}

export interface SkillListDto {
  pageInfo: PageInfoDto;
  list: SkillItemDto[];
}

export interface SkillListParams extends PageParams {
  categoryId?: number;
  tagId?: number;
  keyword?: string;
  sortType?: "all" | "download" | "like" | "install" | "name";
}

export interface SkillCategoryDto {
  id: number;
  name: string;
  icon: string;
  color: string;
  desp: string;
  sort: number;
  createTime: string;
  updateTime: string;
  deleted: boolean;
  deleteTime: string | null;
}

export interface SkillCategoryListDto {
  pageInfo: PageInfoDto;
  list: SkillCategoryDto[];
}

export const getSkillList = (
  params: SkillListParams,
): Promise<ApiResponse<SkillListDto>> => {
  return request.get("/skill/list", { params }) as Promise<ApiResponse<SkillListDto>>;
};

export const getSkillTop50 = (
  params: PageParams,
): Promise<ApiResponse<SkillTop50Dto>> => {
  return request.get("/skill/top50", { params }) as Promise<ApiResponse<SkillTop50Dto>>;
};

export const getSkillCategoryList = () => {
  return request.get<ApiResponse<SkillCategoryListDto>>("/skill/category/list");
};

export const getRecommendSkill = (): Promise<ApiResponse<SkillItemDto>> => {
  return request.get("/skill/getRecommendSkill") as Promise<ApiResponse<SkillItemDto>>;
};

export const getSwiperList = (): Promise<ApiResponse<unknown[]>> => {
  return request.get("/skill/swiperList") as Promise<ApiResponse<unknown[]>>;
};

// —— Subscribe / Trade ——

export interface SubscribePlanItem {
  title?: string;
  price?: number;
  description?: string;
  recommended?: boolean;
  [key: string]: unknown;
}

export interface PlanListDto {
  list?: SubscribePlanItem[];
}

export function getPlanList(type: string | number): Promise<ApiResponse<PlanListDto>> {
  return request.get("/products/plan/list", {
    params: { type },
  }) as Promise<ApiResponse<PlanListDto>>;
}

export function createSubscriptionOrder(params: Record<string, unknown>) {
  return request.post("/trade/subscription/payByGatewayAliPay", {
    origin: "skillhub",
    ...params,
  });
}

export function createSubscriptionOrderByWxPay(params: Record<string, unknown>) {
  return request.post("/trade/subscription/payByGatewayWxPay", {
    origin: "skillhub",
    ...params,
  });
}

export function checkGatewayPayment(params: Record<string, unknown>) {
  return request.post("/trade/subscription/checkGatewayPayment", params);
}

export function checkGatewayWxPayment(params: Record<string, unknown>) {
  return request.post("/trade/subscription/checkGatewayWxPayment", params);
}

export function createPointsOrder(params: Record<string, unknown>) {
  return request.post("users/pointPackage/payByGatewayAliPay", {
    origin: "skillhub",
    ...params,
  });
}

export function createPointsOrderByWxPay(params: Record<string, unknown>) {
  return request.post("users/pointPackage/payByGatewayWxPay", {
    origin: "skillhub",
    ...params,
  });
}

export function checkPointsPayment(params: Record<string, unknown>) {
  return request.post("users/pointPackage/checkGatewayPayment", params);
}

export function checkPointsWxPayment(params: Record<string, unknown>) {
  return request.post("users/pointPackage/checkGatewayWxPayment", params);
}

export function getPointPackageList() {
  return request.get("users/pointPackage/list");
}

// —— Points ——

export type PointsChangeType = "debit" | "credit";

export interface GetUserPointsLogListParams {
  changeType?: PointsChangeType;
  page?: number;
  pageSize?: number;
}

export function getUserPointsTotal() {
  return request.get("/users/points/total");
}

export function getUserPointsLogList(params: GetUserPointsLogListParams) {
  return request.get("/users/userPointsLog/list", { params });
}

export function getInviteCodeList(params: { page?: number; pageSize?: number }) {
  return request.get("/users/invite/code/list", { params });
}

export function getInviteCodeLogList() {
  return request.get("/users/invite/code/log/list");
}

// —— Gateway ——

export interface GatewayApikeyItemDto {
  id: number;
  code?: string;
  apikey: string;
  status: number;
  createTime?: string | number;
  updateTime?: string | number;
}

export interface GatewayApikeyListDto {
  list: GatewayApikeyItemDto[];
  pageInfo?: {
    total?: number;
  };
}

export const getGatewayApikeyList = (
  params: PageParams,
): Promise<ApiResponse<GatewayApikeyListDto>> => {
  return request.get("/gateway/apikey/list", { params });
};

export interface SetGatewayApikeyStatusPayload {
  id: number;
  enabled: boolean;
}

export const setGatewayApikeyStatus = (
  payload: SetGatewayApikeyStatusPayload,
): Promise<ApiResponse<{ status?: number }>> => {
  return request.post("/gateway/apikey/setTokenStatus", payload);
};

// —— App ——

export interface AppDownloadLink {
  url: string;
}

export interface AppChangeLogLocale {
  content: string[];
  title: string;
}

export interface AppLatestDto {
  id: number;
  product: string;
  channel: string;
  version: string;
  date: string;
  changeLog: {
    ch: AppChangeLogLocale;
    en: AppChangeLogLocale;
  };
  macIntel: AppDownloadLink | null;
  macArm: AppDownloadLink | null;
  windowsX64: AppDownloadLink | null;
}

export function getAppLatest(): Promise<ApiResponse<AppLatestDto>> {
  return request.get("app/latest") as Promise<ApiResponse<AppLatestDto>>;
}

// —— Content ——

export const getUserTerms = () => {
  return request.get("content/userTerms/latest");
};

export const getPrivacyPolicy = () => {
  return request.get("content/privacyPolicy/latest");
};
// 客户端参数配置（返回内层 data）
export const getParamConfig = (): any => {
  return request.get("param/config").then((res: any) => {
    if (res?.data == null) {
      throw new Error("param/config: missing data");
    }
    return res.data;
  });
};

// 获取最新协议
export const getLatestPrivacyPolicy = (params: any): Promise<unknown> => {
  return request.get('content/privacyPolicy/latest', { params });
}

// 微信扫码新用户绑定手机号
export const wxghRegisterByPhone = (params: {
  registerToken: string;
  phone: string;
  code: string;
  inviteCode?: string;
}): any => {
  return request.post("/auth/wxghRegisterByPhone", params);
};